import { Box, Fade, Popper as MuiPopper } from '@mui/material';
import { ReactNode } from 'react';

import { usePresentationChartHover } from '@/modules/presentation/components/PresentationHoverActions/PresentationHoverActions.hooks';
import { usePresentationEditingContext } from '@/modules/presentation/context/PresentationEditing.context';
import { HOVER_HALO_OUTLINE } from '@/styles/tokens/colors';
import { zIndexes } from '@/styles/zIndexes';

import { useClientPresentationDesignerContext } from '../../clientPresentation/ClientPresentationDesigner.context';
import { EntityDiagramChartHoverToolbar } from './EntityDiagramChartHoverToolbar';

interface EntityDiagramChartHoverProps {
  /** Open the full-screen entity-diagram editor. */
  onEditDiagram: () => void;
  /**
   * The editor trowser is open — suppress the diagram-level treatment so the
   * "Edit diagram" toolbar doesn't render over it or flash as it opens.
   */
  isEditDiagramOpen: boolean;
  /** The diagram card (legend + border + diagram). */
  children: ReactNode;
}

/**
 * Diagram-level hover treatment for the presentation entity diagram: a blue
 * halo around the whole diagram card — its legend and border included, not just
 * the diagram canvas — plus a floating "Edit diagram" toolbar, shown while the
 * card is hovered and no editable tile is. The toolbar is a portaled Popper, so
 * — like the tile toolbars — it stays at native size instead of scaling with
 * the slide's transform.
 *
 * Inert unless rendered inside a `PresentationHoverActionsProvider` (i.e. only
 * in design mode). Suppressed while the editor trowser is open and for view-only
 * collaborators. (In presentation mode node clicks are a no-op, so there's no
 * node summary panel to suppress against.)
 *
 * Mirrors the estate-waterfall `EstateWaterfallChartHover`.
 */
export function EntityDiagramChartHover({
  onEditDiagram,
  isEditDiagramOpen,
  children,
}: EntityDiagramChartHoverProps) {
  // Prefer the injected editing context (e.g. the single-entity export preview);
  // fall back to the designer context so the presentation designer is unchanged.
  const editing = usePresentationEditingContext();
  const { canUserEditData } = useClientPresentationDesignerContext();
  const canEdit = editing?.canEdit ?? canUserEditData;
  const { isActive, anchorRef, containerProps, toolbarProps } =
    usePresentationChartHover({
      disabled: isEditDiagramOpen || !canEdit,
    });

  return (
    <Box
      ref={anchorRef}
      {...containerProps}
      sx={{ height: '100%', ...(isActive ? HOVER_HALO_OUTLINE : {}) }}
    >
      {children}
      {isActive && anchorRef.current && (
        <MuiPopper
          open
          transition
          anchorEl={anchorRef.current}
          placement="top-start"
          modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
          sx={{ zIndex: zIndexes.POPOVER }}
          onMouseEnter={toolbarProps.onMouseEnter}
          onMouseLeave={toolbarProps.onMouseLeave}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={125}>
              <div>
                <EntityDiagramChartHoverToolbar onEdit={onEditDiagram} />
              </div>
            </Fade>
          )}
        </MuiPopper>
      )}
    </Box>
  );
}
